import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Body,
  Button,
  Card,
  CardTitleWithIcon,
  Heading,
  Link,
  LoadingSpinnerCenter,
  TrevorIconName,
} from '@trevor/ui-components';
import { useDatasourceId } from '@/hooks';
// eslint-disable-next-line camelcase
import { SUBSCRIPTION } from '@/features/credits/utils';
import {
  DiscussPricingText,
  SubscriptionDescriptionContainer,
  SubscriptionFeaturesContainer,
} from '@/features/credits/components/YourPlanWidget/YourPlanWidget.styles';
import { TSubscription } from '@/features/credits/types';
import { userAction } from '@/util/helpers/ajax-helpers';
import { useIntercom } from 'react-use-intercom';
import { useGetSubscriptionDetailsQuery } from '../../apiSlice';

const SUBSCRIPTION_ICON: Record<TSubscription, TrevorIconName> = {
  [SUBSCRIPTION.free]: 'illustration-free-plan',
  [SUBSCRIPTION.pro]: 'illustration-pro-plan',
  [SUBSCRIPTION.enterprise]: 'illustration-enterprise',
};

const SUBSCRIPTION_FEATURES: Record<TSubscription, string[]> = {
  [SUBSCRIPTION.free]: ['All features', 'Unlimited users'],
  [SUBSCRIPTION.pro]: ['All features', 'Unlimited users'],
  [SUBSCRIPTION.enterprise]: [
    'Single sign-on',
    'Audit Logs',
    'All core features',
    'Unlimited users',
    'Support for self-hosted (contact us)',
  ],
};

const defaultSubscription = { name: '', totalCredits: 0 };

export const YourPlanWidget: React.FC = () => {
  const datasourceId = useDatasourceId();
  const navigate = useNavigate();
  const { show: showIntercom } = useIntercom();
  const { data: subscription = defaultSubscription, isFetching } =
    useGetSubscriptionDetailsQuery(datasourceId);

  const isEnterprise = subscription.name === SUBSCRIPTION.enterprise;
  const availableFeatures =
    SUBSCRIPTION_FEATURES[subscription.name as TSubscription];

  const handleNavigateToBilling = () => {
    navigate(`/datasources/${datasourceId}/billing`);
  };

  const handleLiveSupport = () => {
    userAction('live-chat-support-clicked', {
      section: 'credit-page-subscription-plan',
    });
    showIntercom();
  };

  return (
    <Card padding={48} gap={24} width="336px" minHeight="480px">
      {isFetching ? (
        <LoadingSpinnerCenter size="lg" />
      ) : (
        <>
          <CardTitleWithIcon
            iconName={SUBSCRIPTION_ICON[subscription.name as TSubscription]}
            title="Your plan"
          />
          <SubscriptionDescriptionContainer>
            <Heading variant={isEnterprise ? 2 : 1}>
              {subscription.name.toUpperCase()}
            </Heading>
          </SubscriptionDescriptionContainer>
          <SubscriptionFeaturesContainer>
            <Body variant={1} weight="bold">
              Includes
            </Body>
            <ul>
              {availableFeatures.map((feature) => (
                <li key={feature}>
                  <Body variant={1}>{feature}</Body>
                </li>
              ))}
            </ul>
          </SubscriptionFeaturesContainer>
          <Button size="large" expanded onClick={handleNavigateToBilling}>
            {subscription.name === SUBSCRIPTION.free
              ? 'Add billing info'
              : 'Billing information'}
          </Button>
          <DiscussPricingText>
            To discuss fixed pricing please reach out to the team&nbsp;
            <Link onClick={handleLiveSupport}>via chat.</Link>
          </DiscussPricingText>
        </>
      )}
    </Card>
  );
};
